import React from "react";
import { spring, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { SvgIcon } from "../../components/SvgComponents";

interface IntroProps {
  headline: string;
  keywords?: string[];
  introIcon?: string;
}

export const GridMotionIntro: React.FC<IntroProps> = ({
  headline,
  keywords = [],
  introIcon,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Intro Card Pop-in spring
  const introScale = spring({
    frame,
    fps,
    from: 0.82,
    to: 1.0,
    config: { damping: 12, mass: 0.6, stiffness: 110 },
  });

  const introOpacity = interpolate(frame, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const words = headline.split(" ");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        padding: "0 60px",
        boxSizing: "border-box",
      }}
    >
      {/* Intro Icon with Spinning Target HUD */}
      {introIcon && (
        <div
          style={{
            position: "relative",
            marginBottom: "40px",
            transform: `scale(${spring({ frame, fps, from: 0, to: 1, config: { damping: 10 } })})`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Circular HUD scanner rings around the icon */}
          <div
            style={{
              position: "absolute",
              width: "160px",
              height: "160px",
              border: "1.5px dashed rgba(255, 0, 127, 0.4)",
              borderRadius: "50%",
              transform: `rotate(${frame * 2.5}deg)`,
              boxShadow: "0 0 15px rgba(255, 0, 127, 0.15)",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "140px",
              height: "140px",
              border: "1px dashed rgba(0, 242, 254, 0.4)",
              borderRadius: "50%",
              transform: `rotate(${-frame * 1.5}deg)`,
            }}
          />
          <div
            style={{
              zIndex: 2,
              filter: "drop-shadow(0 0 15px rgba(255, 0, 127, 0.4))",
            }}
          >
            <SvgIcon name={introIcon} width={100} height={100} primaryColor="#ff007f" />
          </div>
        </div>
      )}

      {/* Cyberpunk Hook Card */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          padding: "50px 30px",
          background: "rgba(4, 5, 15, 0.8)",
          backdropFilter: "blur(16px)",
          border: "2px solid rgba(0, 242, 254, 0.35)",
          borderRadius: "16px", // Sharp HUD corners
          boxShadow: "0 25px 50px rgba(0,0,0,0.5), 0 0 35px rgba(0,242,254,0.15)",
          transform: `scale(${introScale})`,
          opacity: introOpacity,
          boxSizing: "border-box",
        }}
      >
        {/* Neon Corners */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "16px", height: "16px", borderLeft: "3px solid #ff007f", borderTop: "3px solid #ff007f" }} />
        <div style={{ position: "absolute", top: 0, right: 0, width: "16px", height: "16px", borderRight: "3px solid #ff007f", borderTop: "3px solid #ff007f" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, width: "16px", height: "16px", borderLeft: "3px solid #bef264", borderBottom: "3px solid #bef264" }} />
        <div style={{ position: "absolute", bottom: 0, right: 0, width: "16px", height: "16px", borderRight: "3px solid #bef264", borderBottom: "3px solid #bef264" }} />

        {/* Ambient background glow inside the card */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(255, 0, 127, 0.18) 0%, transparent 75%)",
            transform: "translate(-50%, -50%)",
            filter: "blur(40px)",
            zIndex: -1,
            pointerEvents: "none",
          }}
        />

        {/* Headline words */}
        <h1
          style={{
            fontFamily: "var(--font-title)",
            fontSize: "80px",
            fontWeight: 900,
            color: "#ffffff",
            textAlign: "center",
            margin: 0,
            lineHeight: 1.05,
            letterSpacing: "-2px",
            textShadow: "0 10px 30px rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignContent: "center",
          }}
        >
          {words.map((word, idx) => {
            const wordElapsed = Math.max(0, frame - idx * 2.5);
            const wordScale = spring({
              frame: wordElapsed,
              fps,
              from: 0,
              to: 1,
              config: { damping: 11, mass: 0.5, stiffness: 130 },
            });
            const wordTranslateY = interpolate(wordScale, [0, 1], [50, 0]);
            const wordOpacity = interpolate(wordElapsed, [0, 6], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });

            // Alternate colors for highlighted visual contrast
            const isAlternate = idx % 3 === 2;

            return (
              <span
                key={idx}
                style={{
                  display: "inline-block",
                  margin: "0 12px",
                  transform: `scale(${wordScale}) translateY(${wordTranslateY}px)`,
                  opacity: wordOpacity,
                  background: isAlternate ? "linear-gradient(135deg, #bef264 30%, #00f2fe 100%)" : "none",
                  WebkitBackgroundClip: isAlternate ? "text" : "none",
                  WebkitTextFillColor: isAlternate ? "transparent" : "none",
                  backgroundClip: isAlternate ? "text" : "none",
                  textShadow: isAlternate ? "0 0 15px rgba(190, 242, 100, 0.3)" : "none",
                }}
              >
                {word}
              </span>
            );
          })}
        </h1>
      </div>

      {/* Cyberpunk Keywords below */}
      {keywords.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            justifyContent: "center",
            marginTop: "45px",
            opacity: interpolate(frame, [15, 25], [0, 1], { extrapolateLeft: "clamp" }),
          }}
        >
          {keywords.map((keyword, i) => (
            <div
              key={i}
              style={{
                background: "rgba(4, 5, 15, 0.75)",
                border: "1px solid rgba(0, 242, 254, 0.4)",
                padding: "8px 18px",
                borderRadius: "4px", // Tech square edges
                fontFamily: "var(--font-title)",
                fontSize: "15px",
                fontWeight: 800,
                color: "#00f2fe",
                textTransform: "uppercase",
                letterSpacing: "2.5px",
                boxShadow: "0 0 10px rgba(0, 242, 254, 0.15)",
                position: "relative",
              }}
            >
              <span style={{ position: "absolute", left: 0, top: 0, width: "3px", height: "3px", background: "#ff007f" }} />
              <span style={{ position: "absolute", right: 0, bottom: 0, width: "3px", height: "3px", background: "#ff007f" }} />
              #{keyword}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
